import {
 updateFftListsService,
 fetchFftLists,
 createFftList,
 deleteFftList,
 deleteArchivedFftList,
 updateFftListService,
 saveOrArchiveFormulation,
 fetchFftList,
 fetchMultipleFftLists,
 unarchiveFormulation,
 copyFftList,
} from "../../services/fftList/fftListService";
import { queryClient } from "../../services/utils";

const state = {
 fftLists: [],
 archivedFftLists: [],
 selectedFftList: null,
 selectedArchivedFftList: null,
 fftListChanged: false,
 fftCachedData: null,
 fftCachedDataExpiry: null,
};

const mutations = {
 SET_FFT_LISTS(state, items) {
  state.fftLists = items;
 },
 SET_SELECTED_FFT(state, item) {
  state.selectedFftList = item;
 },
 SET_FFT_LIST_CHANGED(state, value) {
  state.fftListChanged = value;
 },
 REMOVE_FFT_LIST(state, itemIndex) {
  state.fftLists.splice(itemIndex, 1);
 },

 SET_ARCHIVED_FFT_LISTS(state, items) {
  state.archivedFftLists = items;
 },
 SET_SELECTED_ARCHIVED_LIST(state, item) {
  state.selectedArchivedFftList = item;
 },

 REMOVE_ARCHIVED_FFT_LIST(state, itemIndex) {
  state.archivedFftLists.splice(itemIndex, 1);
 },

 SET_FFT_CACHE(state, payload) {
  state.fftCachedData = payload;
 },

 SET_FFT_CACHE_EXPIRY(state, payload) {
  state.fftCachedDataExpiry = payload;
 },
};

const actions = {
 resetFftListsCache({}) {
  queryClient.removeQueries(["FETCH_RECIPE_LISTS"], { exact: false });
  queryClient.removeQueries(["FETCH_NUTR_SPEC_LISTS"], { exact: false });
 },

 async createFftList({ commit }, requestBody) {
  try {
   const response = await createFftList(requestBody);

   if (response.status === 200) {
    let last_pinned_index = state.fftLists.findLastIndex(x => x.is_pinned);
    const fftLists = state.fftLists.toSpliced(last_pinned_index + 1, 0, response.data.data);
    commit("SET_FFT_LISTS", fftLists);
   }
   return response;
  } catch (e) {
   console.error(e);
  }
 },

 async copyFftList({ commit }, { fftListId, requestBody }) {
  try {
   const response = await copyFftList(fftListId, requestBody);
   if (response.status === 200) {
    let last_pinned_index = state.fftLists.findLastIndex(x => x.is_pinned);
    const fftLists = state.fftLists.toSpliced(last_pinned_index + 1, 0, response.data.data);
    commit("SET_FFT_LISTS", fftLists);
   }
   return response;
  } catch (e) {
   console.error(e);
  }
 },

 async saveOrArchiveFormulation({ dispatch }, requestBody) {
  try {
   const response = await saveOrArchiveFormulation(requestBody);

   if (response?.status === 200) {
    dispatch("fetchFftLists", { isArchived: requestBody.action == 'archive' });
    dispatch("resetFftListsCache");
   }
   return response;
  } catch (e) {
   console.error(e);
   throw e;
  }
 },
 async unarchiveFormulation({ dispatch }, requestBody) {
  try {
   const response = await unarchiveFormulation(requestBody);

   if (response.status === 200) {
    dispatch("fetchFftLists", { isArchived: false });
    dispatch("resetFftListsCache");
   }
   return response;
  } catch (e) {
   console.error(e);
  }
 },

 async updateFftList({ dispatch, commit }, { fftListId, requestBody }) {
  try {
   const response = await updateFftListService(fftListId, requestBody);

   if (response.status === 200) {
    const fftLists = [...state.fftLists];
    const fftListIndex = fftLists.findIndex(fftList => fftList.id === fftListId);
    fftLists[fftListIndex] = response?.data?.data;
    const parent_id = response?.data?.data?.version_of_id;
    if (parent_id) {
     const parent = (await fetchFftList(parent_id)).data.data;
     const parentIndex = fftLists.findIndex(fftList => fftList.id === parent_id);
     fftLists[parentIndex] = parent;
    }
    commit("SET_FFT_LISTS", fftLists);
    dispatch("resetFftListsCache");
    dispatch("clearCachedFftData");
   }

   return response;
  } catch (e) {
   console.error(e);
   throw e;
  }
 },

 async fetchFftLists({ commit }, { isArchived = false, includeDetails = false }) {
  try {
   const response = await fetchFftLists({ isArchived, includeDetails });
   const fftLists = response.data.data;

   if (isArchived) {
    commit("SET_ARCHIVED_FFT_LISTS", fftLists);
   } else {
    commit("SET_FFT_LISTS", fftLists);
   }
  } catch (e) {
   console.error(e);
  }
 },

 async fetchFftList({}, { fftListId }) {
  try {
   const response = await fetchFftList(fftListId);
   const fftList = response.data.data;
   return fftList;
  } catch (e) {
   console.error(e);
  }
 },

 async fetchMultipleFftLists({}, { params }) {
  try {
   let response = await fetchMultipleFftLists(params);
   return response.data.data;
  } catch (e) {
   console.error(e);
  }
 },

 async deleteFftList({ commit, dispatch }, { fftListId, archive, deleteAssociatedLists = false }) {
  try {
   let response = null;
   if (!archive) {
    response = await deleteFftList(fftListId, deleteAssociatedLists);
   } else {
    response = await deleteArchivedFftList(fftListId);
   }
   if (response.status === 200) {
    const fftLists = [...state.fftLists];
    const fftListIndex = fftLists.findIndex(fftList => fftList.id === fftListId);
    fftLists.splice(fftListIndex, 1);

    commit("SET_FFT_LISTS", fftLists);
    dispatch("clearCachedFftData");
   }
  } catch (error) {
   console.error(error);
  }
 },

 updateSelectedFftList({ commit }, fftList) {
  commit("SET_SELECTED_FFT", fftList);
 },

 updateSelectedArchivedList({ commit }, archivedList) {
  commit("SET_SELECTED_ARCHIVED_LIST", archivedList);
 },

 isFftListChanged({ commit }, value) {
  commit("SET_FFT_LIST_CHANGED", value);
 },

 async updateFftLists({ commit, dispatch }, fftLists) {
  await updateFftListsService(fftLists);
  dispatch("fetchFftLists", { isArchived: false });
  dispatch("clearCachedFftData");
 },

 removeFftListFromStore({ commit, dispatch }, { rowIndex }) {
  commit("REMOVE_FFT_LIST", rowIndex);
 },

 removeArchivedListFromStore({ commit, dispatch }, { rowIndex }) {
  commit("REMOVE_ARCHIVED_FFT_LIST", rowIndex);
 },

 cacheFftDataTemporarily({ commit }, payload) {
  commit("SET_FFT_CACHE", payload);

  const expiryTime = new Date().getTime() + 10 * 60000;
  commit("SET_FFT_CACHE_EXPIRY", expiryTime);
 },

 clearCachedFftData({ commit }) {
  commit("SET_FFT_CACHE", null);
  commit("SET_FFT_CACHE_EXPIRY", null);
 },
};

const getters = {
 getFftLists: state => state.fftLists,
 getSelectedFftList: state => state.selectedFftList,
 getIsFftListChanged: state => state.fftListChanged,
 getArchivedFftLists: state => state.archivedFftLists,
 getSelectedArchivedFftList: state => state.selectedArchivedFftList,
 getFftCachedData: state => state.fftCachedData,
 getFftCachedDataExpiry: state => state.fftCachedDataExpiry,
};

export default {
 namespaced: true,
 state,
 getters,
 actions,
 mutations,
};
